// Angular
import { Observable, takeUntil } from 'rxjs';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';

// Libraries
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { addDays, subDays } from 'date-fns';

import { saveAs } from 'file-saver';

import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';

// Services
import { JobCommentService } from '../../../../../comment/services/job-comment.service';
import { ValidationService } from '../../../../../../shared/services/validation.service';
import { TimezoneService } from '../../../../../job/services/timezone.service';
import { JobService } from '../../../../../job/services/job.service';
import { CoreService } from '../../../../../../shared/services/core.service';
import { JobAttachmentService } from '../../../../../job/services/job-attachment.service';
import { DialogService } from '../../../../../../shared/services/dialog.service';
import { SpinnerService } from '../../../../../../shared/components/spinner/services/spinner.service';

// Models
import { Job } from '../../../../../job/models';
import { JobCommentChannelDto } from '../../../../../job/models/job-comment-channel-dto';
import { JobCommentDto } from '../../../../../job/models/job-comment-dto';
import { AppCleanup, DynamicMenuButton, DynamicTableColumn, LookUpResponse } from '../../../../../../shared/models';
import { JobTypeChannelDto } from '../../models/job-type-channel-dto';
import { JobCommentDefaultResponseAllDto } from '../../../../../comment/models/job-comment-default-response-all-dto';
import { JobAttachmentViewModel } from '../../../../../job/models/job-attachment.model';
import { ConfirmDialogModel } from '../../../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Components

// Store
import { LookupState } from '../../../../../../shared/state/lookup.state';

// Enums
import { StatusType } from '../../../../../../shared/enums';
import { JobCommentDefaultDto } from '../../../../../comment/models/job-comment-default-dto';
import { StorageState } from '../../../../../../shared/state/storage.state';
import { JobCommentDefaultGroupLocalStorage } from '../../../../../comment/models/job-comment-default-group-dto';

@Component({
	selector: 'comment-add-dialog',
	templateUrl: './comment-add-dialog.component.html',
	styleUrls: ['./comment-add-dialog.component.scss'],
})
export class CommentAddDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	@Select(LookupState.lookups) lookups$: Observable<LookUpResponse>;
	@Select(StorageState.commentGroups) commentGroups$: Observable<JobCommentDefaultGroupLocalStorage[]>;
	readonly matDialogRef: MatDialogRef<CommentAddDialogComponent>;
	form: FormGroup;
	eventName: string;
	maxDate: Date;
	minDate: Date;
	jobTypeChannels: JobTypeChannelDto[] = [];
	extraJobTypeChannels: JobCommentChannelDto[] = [];
	jobCommentGroups: JobCommentDefaultResponseAllDto;
	showSpinner = true;
	editCommentCategory = false;
	replacementValue = 1;
	editedJobCommentDefaultId: number;
	jobId: number;
	attachments: JobAttachmentViewModel[];
	attachmentsAdded: number[] = [];
	columnList: DynamicTableColumn[] = [
		{
			name: 'fileName',
			display: 'File Name',
			width: 1,
		},
		// {
		// 	name: 'jobAttachmentType.name',
		// 	display: 'Type',
		// 	width: 1,
		// },
		{
			name: 'displayCreateAt',
			display: 'Date created',
			isDefaultSortColumn: true,
			sortOnPropertyName: 'createdAt',
			defaultSortDirection: 'desc',
			width: 1,
		},
	];
	menuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'image_search',
			name: 'View',
			event: this.viewAttachment.bind(this),
			condition: 'isImage',
		},
		{
			matIcon: 'download',
			name: 'Download',
			event: this.downloadAttachment.bind(this),
		},
		{
			matIcon: 'close',
			name: 'Delete',
			event: this.deleteAttachment.bind(this),
		},
	];

	constructor(
		private dialogRef: MatDialogRef<CommentAddDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			jobComment: JobCommentDto;
			job: Job;
			source: string;
			hideSaveActions: boolean;
		},
		private formBuilder: FormBuilder,
		private datePipe: DatePipe,
		private timezoneService: TimezoneService,
		private jobCommentService: JobCommentService,
		private jobAttachmentService: JobAttachmentService,
		private jobService: JobService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private spinnerService: SpinnerService,
		private dialogService: DialogService
	) {
		super();
	}

	ngOnInit() {
		this.jobCommentService
			.getCommentGroups()
			.pipe(takeUntil(this.destroy))
			.subscribe((res: JobCommentDefaultResponseAllDto) => {
				this.jobCommentGroups = res;
			});

		this.minDate = subDays(this.displayTimeZone(this.data.job.start), 7);
		this.maxDate = addDays(this.displayTimeZone(this.data.job.end), 7) ?? new Date();
		// Adding for mobile so that page isn't accidentally clicked in
		// This does delay the page from opening for 500ms.
		if (this.data.source === 'mobile') {
			setTimeout(() => {
				this.showSpinner = false;
			}, 750);
		} else {
			this.showSpinner = false;
		}

		this.eventName = this.data.jobComment.jobCommentDefault?.overrideComment ? this.data.jobComment.jobCommentDefault?.overrideComment : this.data.jobComment.jobCommentDefault?.comment;
		this.editedJobCommentDefaultId = this.data.jobComment.jobCommentDefault?.overrideComment ? null : this.data.jobComment.jobCommentDefault?.id;
		if (this.data.jobComment.jobCommentDefault?.replacements) {
			let replacementArray = this.data.jobComment.jobCommentDefault.replacements.split(',');

			replacementArray.forEach((ra) => {
				let replacementParts = ra.split(':');

				if (replacementParts.length === 2) {
					if (!this.data.jobComment.id && this.eventName.includes(replacementParts[0])) {
						this.replacementValue = 1;

						this.eventName = this.eventName.replace(replacementParts[0], String(this.replacementValue));
					} else if (!this.data.jobComment.id && this.eventName.includes('#') && this.eventName.includes(')')) {
						let firstIndex = this.eventName.indexOf('#');
						let lastIndex = this.eventName.indexOf(')');

						if (firstIndex >= 0 && lastIndex > firstIndex) {
							let findNumber = this.eventName.substring(firstIndex + 1, lastIndex);
							this.replacementValue = isNaN(+findNumber) ? 1 : parseInt(findNumber);
						}
					} else if (this.data.jobComment.id) {
						if (this.data.jobComment.replacements) {
							let jobCommentPlacementArray = this.data.jobComment.replacements.split(',');

							jobCommentPlacementArray.forEach((jcpa) => {
								let jobCommentReplacementParts = jcpa.split('=');

								if (jobCommentReplacementParts.length === 2) {
									this.eventName = this.eventName.replace(jobCommentReplacementParts[0], jobCommentReplacementParts[1]);
									this.replacementValue = isNaN(+jobCommentReplacementParts[1]) ? 1 : +jobCommentReplacementParts[1];
								}
							});
						}
					}
				}
			});
		}

		if (!this.eventName) {
			this.eventName = 'Custom Comment';
		}

		this.lookups$.pipe(takeUntil(this.destroy)).subscribe((lookups) => {
			if (lookups) {
				this.jobTypeChannels = lookups.jobTypeChannels.filter((jtc) => jtc.jobTypeId == this.data.job.jobTypeId);
			}
		});

		this.extraJobTypeChannels = [];

		this.jobTypeChannels
			.filter((jobTypeChannel) => !jobTypeChannel.channelId && !jobTypeChannel.parentId)
			.forEach((jtc) => {
				let extraJobTypeChannel: JobCommentChannelDto = {
					id: 0,
					jobCommentId: this.data.jobComment.id,
					jobTypeChannelId: jtc.id,
					jobTypeChannel: jtc,
					value: null,
					children: [],
				};

				jtc.inverseParent.forEach((ip) => {
					extraJobTypeChannel.children.push({
						id: 0,
						jobCommentId: this.data.jobComment.id,
						jobTypeChannelId: ip.id,
						jobTypeChannel: ip,
						value: null,
						children: [],
					});
				});
				this.extraJobTypeChannels.push(extraJobTypeChannel);
			});

		this.createForm();

		this.getManualEntryValues(new Date(this.data.jobComment.commentDate)?.getTime() ?? this.maxDate?.getTime());

		this.jobId = this.data.jobComment.jobId;

		if (this.data?.jobComment?.id) {
			this.jobService
				.getCommentAttachments(this.jobId, this.data.jobComment.id)
				.pipe(takeUntil(this.destroy))
				.subscribe((attachments) => {
					this.attachments = attachments;
				});
		} else {
			this.attachments = [];
		}
	}

	ngOnDestroy() {
		this.checkForUnusedAttachments();
		super.ngOnDestroy();
	}

	changeEventName(commentDefaultId) {
		let selectedCommentDefault: JobCommentDefaultDto;
		for (let i = 0; i < this.jobCommentGroups.jobCommentDefaultGroups.length; i++) {
			for (let j = 0; j < this.jobCommentGroups.jobCommentDefaultGroups[i].jobCommentDefaults.length; j++) {
				if (this.jobCommentGroups.jobCommentDefaultGroups[i].jobCommentDefaults[j].id === commentDefaultId) {
					selectedCommentDefault = this.jobCommentGroups.jobCommentDefaultGroups[i].jobCommentDefaults[j];
				}
			}
		}

		this.eventName = selectedCommentDefault.comment;
		this.editCommentCategory = false;
	}

	checkForUnusedAttachments() {
		if (this.attachmentsAdded.length > 0) {
			const confirmModel: ConfirmDialogModel = {
				title: 'Delete attachment',
				message: 'You created attachments that are not assigned to this comment. Do you want to delete these attachments?',
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result1) => {
					if (result1) {
						this.spinnerService.showAppSpinner(); // Turns on app wide spinner
						this.spinnerService;
						this.jobService
							.deleteJobAttachments(this.jobId, this.attachmentsAdded)
							.pipe(takeUntil(this.destroy))
							.subscribe(
								(result2) => {
									this.jobService.refreshAttachments.next(true);
									this.attachmentsAdded = [];
									this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
									const message = `${this.translateService.instant('GENERIC.DELETED_PLURAL', { name: this.translateService.instant('MODULE.ATTACHMENT.TITLE_CAPITAL_PLURAL') })}`;
									this.coreService.toast({ message: message, type: StatusType.SUCCESS });
								},
								(error) => {
									this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
									const message = `${this.translateService.instant('GENERIC.NOT_DELETED_PLURAL', { name: this.translateService.instant('MODULE.ATTACHMENT.TITLE_CAPITAL_PLURAL') })}`;
									this.coreService.toast({ message: message, type: StatusType.DANGER });
								}
							);
					}
				});
		}
	}

	displayTimeZone(date: Date): Date {
		return this.timezoneService.timeZoneDate(date, this.data.job?.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone());
	}

	convertToUnixTime(date: Date): Date {
		return this.timezoneService.utcDate(date, this.data.job?.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone());
	}

	private createForm() {
		let fbGroups: any = {};
		fbGroups['commentDate'] = [this.displayTimeZone(this.data.jobComment.commentDate) ?? this.displayTimeZone(this.maxDate), Validators.required];
		fbGroups['comment'] = [this.data.jobComment.comment, [this.customCommentValidation.bind(this)]];
		fbGroups['hideInPortal'] = [this.data.jobComment.hideInPortal];

		this.extraJobTypeChannels.forEach((ejtc) => {
			if (ejtc.jobTypeChannel.isParent) {
				ejtc.children.forEach((c) => {
					fbGroups[c.jobTypeChannel.fieldName] = [
						this.data.jobComment.jobCommentChannels
							?.find((jobCommentChannel) => jobCommentChannel.jobTypeChannelId === ejtc.jobTypeChannelId)
							?.children?.find((jobCommentChannel) => jobCommentChannel.jobTypeChannelId === c.jobTypeChannelId)?.value,
						[Validators.max(999999999), ValidationService.decimalValidation(6)],
					];
				});
			} else {
				fbGroups[ejtc.jobTypeChannel.fieldName] = [
					this.data.jobComment.jobCommentChannels?.find((jobCommentChannel) => jobCommentChannel.jobTypeChannelId === ejtc.jobTypeChannelId)?.value,
					[Validators.max(999999999), ValidationService.decimalValidation(6)],
				];
			}
		});

		this.form = this.formBuilder.group(fbGroups);

		this.form
			.get('commentDate')
			.valueChanges.pipe(takeUntil(this.destroy))
			.subscribe((valueChange) => {
				if (valueChange) {
					this.getManualEntryValues(valueChange.getTime());
				}
			});
	}

	toggleCommentEditMode() {
		this.editCommentCategory = !this.editCommentCategory;
	}

	selectDates() {
		let jobComment: JobCommentDto = {
			id: this.data.jobComment.id,
			jobId: this.data.jobComment.jobId,
			commentDate: this.form.controls.commentDate.value,
			comment: this.form.controls.comment.value,
			jobCommentDefaultId: this.data.jobComment.jobCommentDefaultId,
			jobCommentDefault: this.data.jobComment.jobCommentDefault,
			hideInPortal: this.form.controls.hideInPortal.value,
			replacements: this.data.jobComment.jobCommentDefault?.replacements ? `{x}=${this.replacementValue}` : '',
		};

		this.dialogRef.close({
			jobComment: jobComment,
			action: 'selectDates',
		});
	}

	selectDate() {
		let jobComment: JobCommentDto = {
			id: this.data.jobComment.id,
			jobId: this.data.jobComment.jobId,
			commentDate: this.form.controls.commentDate.value,
			comment: this.form.controls.comment.value,
			jobCommentDefaultId: this.data.jobComment.jobCommentDefaultId,
			jobCommentDefault: this.data.jobComment.jobCommentDefault,
			hideInPortal: this.form.controls.hideInPortal.value,
			replacements: this.data.jobComment.jobCommentDefault?.replacements ? `{x}=${this.replacementValue}` : '',
		};

		this.dialogRef.close({
			jobComment: jobComment,
			action: 'selectDate',
		});
	}

	customCommentValidation(control: FormControl): { [s: string]: boolean } {
		if (!this.data.jobComment.jobCommentDefaultId && !control.value) {
			return { noCustomComment: true };
		}
		return null;
	}

	modifyCommentValue(modifyValue: number) {
		if (this.replacementValue + modifyValue < 1) return;

		if (this.eventName.includes(String(this.replacementValue))) {
			this.eventName = this.eventName.replace(String(this.replacementValue), String(this.replacementValue + modifyValue));
			this.replacementValue += modifyValue;
		}
	}

	getManualEntryValues(commentDate: number) {
		this.jobCommentService
			.getManualEntryValuesForJob(this.data.job.id, commentDate)
			.pipe(takeUntil(this.destroy))
			.subscribe((jobCommentChannels) => {
				if (jobCommentChannels.length > 0) {
					for (let ejtc of this.extraJobTypeChannels) {
						if (ejtc.jobTypeChannel.isParent) {
							ejtc.children.forEach((child) => {
								if (this.form.controls[child.jobTypeChannel.fieldName]) {
									this.form.controls[child.jobTypeChannel.fieldName].setValue(null);
									this.form.controls[child.jobTypeChannel.fieldName].enable();
								}
							});
						} else {
							if (this.form.controls[ejtc.jobTypeChannel.fieldName]) {
								this.form.controls[ejtc.jobTypeChannel.fieldName].setValue(null);
								this.form.controls[ejtc.jobTypeChannel.fieldName].enable();
							}
						}
					}

					jobCommentChannels.forEach((cv) => {
						let ejt = this.extraJobTypeChannels.find((ejtc) => ejtc.jobTypeChannelId == cv.jobTypeChannelId);

						if (!ejt) {
							for (let ejtc of this.extraJobTypeChannels) {
								if (ejtc.jobTypeChannel.isParent) {
									ejt = ejtc.children.find((c) => c.jobTypeChannelId == cv.jobTypeChannelId);
									if (ejt) break;
								}
							}
						}

						if (ejt && this.form.controls[ejt.jobTypeChannel.fieldName]) {
							this.form.controls[ejt.jobTypeChannel.fieldName].setValue(cv.value);
							this.form.controls[ejt.jobTypeChannel.fieldName].disable();
						}
					});
				}
			});
	}

	addAttachment() {
		const dialogRef = this.jobAttachmentService.openFileUploadDialog();

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.spinnerService.showAppSpinner(); // Turns on app wide spinner
					let attachmentName = result.name.split('.').shift();
					let attachmentExtension = result.name.split('.').pop();

					let formData = new FormData();
					formData.append('file', result, result.name);
					formData.append('jobAttachmentTypeId', '2'); // 2 is for Comment

					let fileName = `${attachmentName}.${attachmentExtension}`;
					formData.append('fileName', fileName);

					this.jobService.addJobAttachment(this.jobId, formData).subscribe((attachment) => {
						attachment.displayCreateAt = this.datePipe.transform(this.displayTimeZone(attachment.createdAt), 'M/d/yyyy, h:mm a');
						let attachments = [...this.attachments];
						attachments.push(attachment);
						this.attachments = [...attachments];
						this.attachmentsAdded.push(attachment.id);
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						const message = `${this.translateService.instant('GENERIC.ADDED', { name: this.translateService.instant('MODULE.ATTACHMENT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					});
				}
			});
	}

	deleteAttachment(attachment: JobAttachmentViewModel) {
		const confirmModel: ConfirmDialogModel = {
			title: 'Delete attachment',
			message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.ATTACHMENT.TITLE_CAPITAL') })}`,
			action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result1) => {
				if (result1) {
					this.spinnerService.showAppSpinner();
					this.jobService
						.deleteJobAttachments(this.jobId, [attachment.id])
						.pipe(takeUntil(this.destroy))
						.subscribe((result2) => {
							let attachments = [...this.attachments];
							attachments.splice(
								this.attachments.findIndex((item) => item.id === attachment.id),
								1
							);

							this.attachmentsAdded.splice(
								this.attachmentsAdded.findIndex((id) => id === attachment.id),
								1
							);

							this.attachments = [...attachments];
							this.spinnerService.hideAppSpinner();
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.ATTACHMENT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						});
				}
			});
	}

	downloadAttachment(attachment) {
		this.jobService
			.downloadJobAttachment(this.jobId, attachment.id)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				let filename = attachment.fileName;
				var blob = new Blob([result]);
				saveAs(blob, filename);
			});
	}

	viewAttachment(attachment: JobAttachmentViewModel) {
		this.jobAttachmentService.openImageOrDownloadFile(attachment, false, null);
	}

	submit() {
		this.attachmentsAdded = []; // Resetting array, not to prompt the popup
		this.extraJobTypeChannels.forEach((ejtc) => {
			if (ejtc.jobTypeChannel.isParent) {
				ejtc.children.forEach((c) => {
					c.value = this.form.controls[c.jobTypeChannel.fieldName]?.value;
				});
			} else {
				ejtc.value = this.form.controls[ejtc.jobTypeChannel.fieldName]?.value;
			}
		});

		let jobComment: JobCommentDto = {
			id: this.data.jobComment.id,
			jobId: this.data.jobComment.jobId,
			commentDate: this.convertToUnixTime(this.form.controls.commentDate.value),
			comment: this.form.controls.comment.value,
			jobCommentDefaultId: this.data.jobComment.jobCommentDefaultId,
			hideInPortal: this.form.controls.hideInPortal.value,
			jobCommentChannels: this.extraJobTypeChannels,
			jobAttachments: this.attachments,
			replacements: this.data.jobComment.jobCommentDefault?.replacements ? `{x}=${this.replacementValue}` : '',
		};

		if (this.editCommentCategory) {
			jobComment.jobCommentDefaultId = +this.editedJobCommentDefaultId;
		}

		this.dialogRef.close({
			jobComment: jobComment,
			action: this.data.jobComment.id ? 'update' : 'insert',
		});
	}
}
